import React, { useEffect, useRef } from 'react';

export default function LandingIframe({ onStart, src = '/landing-aireadiness.html' }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      try {
        const win = iframe.contentWindow;
        const doc = win && win.document;
        if (!doc) return;

        // Ensure name/email error placeholders and validators
        const ensureNameEmailValidation = () => {
          const nameInput =
            doc.querySelector('input[name="name"]') ||
            doc.querySelector('#name') ||
            doc.querySelector('input[placeholder*="Full Name" i]') ||
            doc.querySelector('input[placeholder*="Name" i]') ||
            doc.querySelector('input[aria-label*="Name" i]');
          const emailInput =
            doc.querySelector('input[type="email"]') ||
            doc.querySelector('input[name="email"]') ||
            doc.querySelector('#email') ||
            doc.querySelector('input[placeholder*="Email" i]') ||
            doc.querySelector('input[aria-label*="Email" i]');

          const ensureErrorBelow = (inputEl, id, textSample) => {
            if (!inputEl || !inputEl.parentNode) return null;
            let err = doc.getElementById(id);
            if (!err) {
              err = doc.createElement('div');
              err.id = id;
              // Same style as captcha error
              err.setAttribute('style', 'color:#dc2626; font-size:0.9rem; margin-top:0.35rem; display:none;');
              // Insert after input
              try {
                if (inputEl.nextSibling) {
                  inputEl.parentNode.insertBefore(err, inputEl.nextSibling);
                } else {
                  inputEl.parentNode.appendChild(err);
                }
              } catch (_) {}
            }
            return err;
          };

          const nameErr = ensureErrorBelow(nameInput, 'nq-name-error', 'Enter Name');
          const emailErr = ensureErrorBelow(emailInput, 'nq-email-error', 'Enter Email');

          // Live hide on input
          if (nameInput && nameErr) {
            nameInput.addEventListener('input', () => { nameErr.style.display = 'none'; });
          }
          if (emailInput && emailErr) {
            emailInput.addEventListener('input', () => { emailErr.style.display = 'none'; });
          }

          // Expose validator
          win.__nqValidateNameEmail = () => {
            let ok = true;
            if (nameInput && nameErr) {
              const v = (nameInput.value || '').trim();
              if (!v) {
                nameErr.textContent = 'Enter Name';
                nameErr.style.display = 'block';
                ok = false;
              } else {
                nameErr.style.display = 'none';
              }
            }
            if (emailInput && emailErr) {
              const v = (emailInput.value || '').trim();
              if (!v) {
                emailErr.textContent = 'Enter Email';
                emailErr.style.display = 'block';
                ok = false;
              } else {
                emailErr.style.display = 'none';
              }
            }
            return ok;
          };
        };

        // Inject 4-digit captcha after Organization input
        const ensureCaptcha = () => {
          // Try to find organization text box
          const orgInput =
            doc.querySelector('input[name="organization"]') ||
            doc.querySelector('#organization') ||
            doc.querySelector('#org') ||
            doc.querySelector('input[placeholder*="Organization" i]') ||
            doc.querySelector('input[aria-label*="Organization" i]');
          if (!orgInput || !orgInput.parentNode) return;
          if (doc.getElementById('nq-captcha-group')) return;

          // Generate code
          const generateCode = () => Math.floor(1000 + Math.random() * 9000).toString();
          win.__nqCaptchaCode = generateCode();

          const wrapper = doc.createElement('div');
          wrapper.id = 'nq-captcha-group';
          wrapper.style.marginTop = '0.75rem';

          const orgClass = orgInput.className || '';

          wrapper.innerHTML = `
            <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:0.35rem;">
              <label style="font-weight:600; color:#002265;">Security Verification</label>
              <button type="button" id="nq-captcha-refresh" style="background:none;border:none;color:#0ea5e9;cursor:pointer;">↻ Refresh</button>
            </div>
            <div style="display:flex; gap:0.75rem; align-items:center;">
              <div id="nq-captcha-code" style="letter-spacing:0.35rem; font-weight:700; color:#0f172a; background:#f1f5f9; border:1px solid #e2e8f0; border-radius:8px; padding:0.5rem 0.75rem; min-width:7rem; text-align:center;"></div>
              <input id="nq-captcha-input" type="text" inputmode="numeric" maxlength="4" class="${orgClass}" placeholder="Enter code" style="flex:1;"/>
            </div>
            <div id="nq-captcha-error" style="color:#dc2626; font-size:0.9rem; margin-top:0.35rem; display:none;"></div>
            <div id="nq-captcha-success" style="color:#16a34a; font-size:0.9rem; margin-top:0.35rem; display:none;">✅ Verification successful!</div>
          `;

          let inserted = false;
          try {
            if (orgInput.nextSibling) {
              orgInput.parentNode.insertBefore(wrapper, orgInput.nextSibling);
            } else {
              orgInput.parentNode.appendChild(wrapper);
            }
            inserted = true;
          } catch (_) {}

          if (!inserted) {
            // Fallback: place before submit button
            const submitBtn =
              (orgInput.closest && orgInput.closest('form') && orgInput.closest('form').querySelector('button[type="submit"]')) ||
              doc.querySelector('#startForm > button') ||
              doc.querySelector('button[type="submit"]');
            if (submitBtn && submitBtn.parentNode) {
              submitBtn.parentNode.insertBefore(wrapper, submitBtn);
              inserted = true;
            }
          }

          const codeEl = doc.getElementById('nq-captcha-code');
          const inputEl = doc.getElementById('nq-captcha-input');
          const errEl = doc.getElementById('nq-captcha-error');
          const succEl = doc.getElementById('nq-captcha-success');
          const refreshBtn = doc.getElementById('nq-captcha-refresh');

          const renderCode = () => {
            if (codeEl) codeEl.textContent = win.__nqCaptchaCode || '';
          };
          renderCode();

          if (refreshBtn) {
            refreshBtn.addEventListener('click', () => {
              win.__nqCaptchaCode = generateCode();
              renderCode();
              if (inputEl) inputEl.value = '';
              if (errEl) errEl.style.display = 'none';
              if (succEl) succEl.style.display = 'none';
            });
          }

          win.__nqValidateCaptcha = () => {
            const entered = (inputEl?.value || '').replace(/\D/g, '');
            const expected = (win.__nqCaptchaCode || '').toString();
            if (!/^\d{4}$/.test(entered)) {
              if (errEl) {
                errEl.textContent = 'Enter a valid 4-digit code';
                errEl.style.display = 'block';
              }
              if (succEl) succEl.style.display = 'none';
              return false;
            }
            if (entered !== expected) {
              if (errEl) {
                errEl.textContent = 'Incorrect code';
                errEl.style.display = 'block';
              }
              if (succEl) succEl.style.display = 'none';
              return false;
            }
            if (errEl) errEl.style.display = 'none';
            if (succEl) succEl.style.display = 'block';
            return true;
          };

          // Live feedback on input
          if (inputEl) {
            inputEl.addEventListener('input', () => {
              const entered = (inputEl.value || '').replace(/\D/g, '');
              const expected = (win.__nqCaptchaCode || '').toString();
              if (entered.length < 4) {
                if (succEl) succEl.style.display = 'none';
                if (errEl) errEl.style.display = 'none';
                return;
              }
              if (/^\d{4}$/.test(entered) && entered === expected) {
                if (errEl) errEl.style.display = 'none';
                if (succEl) succEl.style.display = 'block';
              } else {
                if (succEl) succEl.style.display = 'none';
              }
            });
          }
        };

        ensureNameEmailValidation();
        ensureCaptcha();

        // If organization field isn't present yet, observe until it's available
        const observeUntilFound = () => {
          try {
            if (doc.getElementById('nq-captcha-group')) return; // already injected
            const formNow = doc.querySelector('#startForm');
            const orgNow =
              doc.querySelector('input[name="organization"]') ||
              doc.querySelector('#organization') ||
              doc.querySelector('#org') ||
              doc.querySelector('input[placeholder*="Organization" i]') ||
              doc.querySelector('input[aria-label*="Organization" i]');
            if (formNow && orgNow) {
              ensureNameEmailValidation();
              ensureCaptcha();
              return;
            }
            const observer = new MutationObserver(() => {
              const form2 = doc.querySelector('#startForm');
              const org2 =
                doc.querySelector('input[name="organization"]') ||
                doc.querySelector('#organization') ||
                doc.querySelector('#org') ||
                doc.querySelector('input[placeholder*="Organization" i]') ||
                doc.querySelector('input[aria-label*="Organization" i]');
              if (form2) {
                try { ensureNameEmailValidation(); } catch (_) {}
              }
              if (form2 && org2 && !doc.getElementById('nq-captcha-group')) {
                try { ensureCaptcha(); } finally { observer.disconnect(); }
              }
            });
            if (doc.body) {
              observer.observe(doc.body, { childList: true, subtree: true });
              // Safety: stop observing after 10s
              setTimeout(() => { try { observer.disconnect(); } catch (_) {} }, 10000);
            }
          } catch (_) {}
        };

        observeUntilFound();

        // Helper to collect user data for startAssessment
        const collectUserData = () => {
          const nameInput =
            doc.querySelector('input[name="name"]') ||
            doc.querySelector('#name') ||
            doc.querySelector('input[placeholder*="Full Name" i]') ||
            doc.querySelector('input[placeholder*="Name" i]') ||
            doc.querySelector('input[aria-label*="Name" i]');
          const emailInput =
            doc.querySelector('input[type="email"]') ||
            doc.querySelector('input[name="email"]') ||
            doc.querySelector('#email') ||
            doc.querySelector('input[placeholder*="Email" i]') ||
            doc.querySelector('input[aria-label*="Email" i]');
          const orgInput =
            doc.querySelector('input[name="organization"]') ||
            doc.querySelector('#organization') ||
            doc.querySelector('#org') ||
            doc.querySelector('input[placeholder*="Organization" i]') ||
            doc.querySelector('input[aria-label*="Organization" i]');
          return {
            name: (nameInput?.value || '').trim(),
            email: (emailInput?.value || '').trim(),
            organization: (orgInput?.value || '').trim()
          };
        };

        // Attach to form submit
        const form = doc.querySelector('#startForm');
        if (form) {
          form.addEventListener('submit', (e) => {
            try {
              e.preventDefault();
              const okFields = typeof win.__nqValidateNameEmail === 'function' ? win.__nqValidateNameEmail() : true;
              if (!okFields) return;
              // Validate captcha if present
              const ok = typeof win.__nqValidateCaptcha === 'function' ? win.__nqValidateCaptcha() : true;
              if (!ok) return;
              if (typeof onStart === 'function') onStart(collectUserData());
            } catch (_) {}
          });
        }

        // Attach to button click
        const btn = doc.querySelector('#startForm > button');
        if (btn) {
          btn.addEventListener('click', (e) => {
            try {
              e.preventDefault();
              const okFields = typeof win.__nqValidateNameEmail === 'function' ? win.__nqValidateNameEmail() : true;
              if (!okFields) return;
              // Validate captcha if present
              const ok = typeof win.__nqValidateCaptcha === 'function' ? win.__nqValidateCaptcha() : true;
              if (!ok) return;
              if (typeof onStart === 'function') onStart(collectUserData());
            } catch (_) {}
          });
        }
      } catch (err) {
        // Cross-origin or other access issue
        console.warn('[LandingIframe] Could not wire start button:', err?.message);
      }
    };

    iframe.addEventListener('load', handleLoad);
    return () => {
      try {
        iframe.removeEventListener('load', handleLoad);
      } catch (_) {}
    };
  }, [onStart]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
      <iframe
        ref={iframeRef}
        title="Assessment Landing"
        src={src}
        style={{
          border: 'none',
          width: '100%',
          height: '100%',
          display: 'block'
        }}
      />
    </div>
  );
}


